import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRouter,
  createRoute,
  createRootRoute,
  redirect,
  RouterProvider,
} from '@tanstack/react-router';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast } from 'sonner';

import { CollectionsPage } from '@/features/collections';
import { LoginPage } from '@/features/auth/LoginPage';
import { clearAuthSession, hasValidAuthSession } from '@/features/auth/session';
import { DevApp } from '@/DevApp';
import { HomeApp } from '@/HomeApp';
import { RagApiError } from '@/services/ragApi';
import '@/index.css';
import '@/lib/fonts.css';

const rootRoute = createRootRoute();

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeApp,
});

const playgroundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/playground',
  component: DevApp,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const collectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collections',
  component: CollectionsPage,
  beforeLoad: () => {
    if (!hasValidAuthSession()) {
      // TanStack's redirect() returns a plain object designed to be thrown
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  playgroundRoute,
  loginRoute,
  collectionsRoute,
]);

const router = createRouter({ routeTree });

function handleUnauthorized() {
  clearAuthSession();
  void router.invalidate();
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      if (err instanceof RagApiError && err.status === 401) handleUnauthorized();
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      if (err instanceof RagApiError && err.status === 401) {
        handleUnauthorized();
      } else if (err instanceof RagApiError && err.status === 403) {
        toast.error('Sem permissão para esta ação.');
      }
    },
  }),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root not found in index.html');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
